import CreateApi from '../../../../shared/Settings/appSettings';
import {
    Box, Stack, Typography,
    Button,
    Checkbox,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTBContext } from '../../../Contexts/useTBStates';
import { RoundButton } from '../../TabComponents';
import { useFetchPaceData, teeTimeMs } from '../../../hooks/useFetchPaceData';
import { effectiveRoundBool } from '../../Functions/applyUniformStartHoleToViewEventData';
import {
    type ShotgunHoleSlot,
    buildDynamicShotgunHoleSlots,
    resolveRoundHoles,
    filterScoresForShotgunSlot,
} from '../../Functions/shotgunTeeSheetUtils';

interface OverviewProps {
    appDims: any;
    compHeights: any;
}

export const FirstTeePanel = ({ appDims, compHeights }: OverviewProps) => {
    const auth = useAuth();
    const api = CreateApi();

    const {
        factor, appTheme, editMode, roundValue, setRoundValue,
        viewEvent, viewEventData, setViewEventData,
        originalViewEventData, paceData,
    } = useTBContext();

    const [teeTimes, setTeeTimes] = useState<any[]>([]);
    const [allowSave, setAllowSave] = useState(false);
    const [teeTimeIndex, setTeeTimeIndex] = useState(0);
    const [teeGroup, setTeeGroup] = useState<any[]>([]);
    const [holeSlots, setHoleSlots] = useState<ShotgunHoleSlot[]>([]);
    const [roundSideGames, setRoundSideGames] = useState<any[]>([]);
    const [paceClockStarting, setPaceClockStarting] = useState(false);

    const { fetchPaceData } = useFetchPaceData();

    useEffect(() => {
        if (viewEventData?.scores?.length > 0 && JSON.stringify(originalViewEventData.current.scores) !== JSON.stringify(viewEventData.scores)) {
            setAllowSave(true);
        } else {
            setAllowSave(false);
        }
    }, [viewEventData.scores, originalViewEventData.current.scores]);

    const isShotgunRound = Boolean(
        viewEvent && effectiveRoundBool(viewEvent.rounds[roundValue - 1]?.shotgunStart),
    );

    function scoresReadyForRound(scores: any[], shotgun: boolean): boolean {
        const roundIdx = roundValue - 1;
        if (scores.length === 0) return false;
        return scores.every((score: any) => {
            const rs = score.roundScores[roundIdx];
            if (rs?.teeTime == null) return false;
            if (shotgun && rs.startingHole == null) return false;
            return true;
        });
    }

    useEffect(() => {
        if (viewEvent && scoresReadyForRound(viewEventData.scores, isShotgunRound)) {
            if (isShotgunRound) {
                refreshShotgunNavigation(viewEventData.scores, viewEventData.registrations);
            } else {
                setHoleSlots([]);
                setTeeTimes(getTeeTimeOptions(viewEventData.scores, viewEventData.registrations));
            }
            setRoundSideGames(viewEvent.rounds[roundValue - 1].sideGames);
        } else {
            setTeeTimes([]);
            setHoleSlots([]);
            setRoundSideGames([]);
            setTeeGroup([]);
            setTeeTimeIndex(0);
            setAllowSave(false);
            // Do not clear originalViewEventData — it is shared across Command Center panels.
        }
    }, [roundValue, viewEventData.scores, viewEventData.registrations, viewEventData.courseData, teeTimeIndex, viewEvent, isShotgunRound]);

    useEffect(() => {
        setTeeTimeIndex(0);
    }, [viewEvent]);

    useEffect(() => {
        if (viewEvent && (viewEvent as any)._id) {
            fetchPaceData(true).catch((err) =>
                console.error('[FirstTeePanel] pace prefetch error', err),
            );
        }
    }, [viewEvent, roundValue, fetchPaceData]);

    function refreshShotgunNavigation(scores: any[], registrations: any[]) {
        if (!viewEvent) return;

        const roundIdx = roundValue - 1;
        const holes = resolveRoundHoles(viewEvent, viewEventData, roundValue);
        const slots = buildDynamicShotgunHoleSlots(holes, registrations, roundValue);
        setHoleSlots(slots);

        const teeTimesUnique = Array.from(
            new Set(scores.map((score: any) => score.roundScores[roundIdx].teeTime)),
        ).sort((a: any, b: any) => new Date(a).getTime() - new Date(b).getTime());
        setTeeTimes(teeTimesUnique);

        const slot = slots[teeTimeIndex];
        if (slot) {
            setTeeGroup(
                filterScoresForShotgunSlot(
                    scores,
                    registrations,
                    roundValue,
                    slot.holeNumber,
                    slot.shotgunOrder,
                ),
            );
        } else {
            setTeeGroup([]);
        }
    }

    function getTeeTimeOptions(scores: any[], registrations: any[]) {
        const teeTimes = scores.length > 0 ? 
            scores.map((score: any) => score.roundScores[roundValue-1].teeTime) : 
            registrations.map((reg: any) => reg.teeTimes[roundValue-1].teeTime);
        const teeTimesUnique = Array.from(new Set(teeTimes));
        const teeTimesUniqueSorted = teeTimesUnique.sort((a: any, b: any) => new Date(a).getTime() - new Date(b).getTime());

        const teeGroup = scores.length > 0 ? 
            scores.filter((score: any) => score.roundScores[roundValue-1].teeTime === teeTimesUniqueSorted[teeTimeIndex]) : 
            registrations.filter((reg: any) => reg.teeTimes[roundValue-1].teeTime === teeTimesUniqueSorted[teeTimeIndex]);
        const teeGroupWithNames = teeGroup.map((score: any, index: number) => {
            const matchingReg = registrations.filter((reg: any) => reg.eventScoreID === score._id.toString());
            return {
                ...score,
                playerName: matchingReg.map((reg: any) => reg.playerName).join('/') || '',
                teamName: matchingReg[0]?.teamName || ''
            };
        });
        setTeeGroup(teeGroupWithNames);

        return teeTimesUniqueSorted;
    }

    async function handleStartPaceClock() {
        const paceTeeTime = isShotgunRound ? teeTimes[0] : teeTimes[teeTimeIndex];
        if (!viewEvent || !paceTeeTime || paceClockStarting) return;

        setPaceClockStarting(true);
        const isOpponent = viewEvent.scoreType?.toLowerCase() === 'opponent';
        const entries: Array<{ scoreCode: string | null }> = isOpponent
            ? teeGroup.map((score: any) => ({ scoreCode: score.playerScoreCode ?? null }))
            : [{ scoreCode: null }];

        try {
            await api.post('/tb/StartPaceClock', {
                eventID: (viewEvent as any)._id,
                roundNumber: roundValue,
                teeTime: paceTeeTime,
                entries,
                userID: auth.user?.profile.sub,
            });
            await fetchPaceData(true);
        } catch (err) {
            console.error('Error starting pace clock', err);
        } finally {
            setPaceClockStarting(false);
        }
    }

    const handleSaveChanges = async () => {

        const updatedScores = JSON.parse(JSON.stringify(viewEventData.scores));
        const orgEventScores = JSON.parse(JSON.stringify(originalViewEventData.current.scores));

        const sideGamesToSave: any[] = [];

        updatedScores.forEach((score: any) => {
            if (score.roundScores[roundValue-1].sideGames !== 
                orgEventScores.find((s: any) => s._id === score._id).roundScores[roundValue-1].sideGames) {
                sideGamesToSave.push({
                    eventScoreID: score._id,
                    roundIndex: roundValue-1,
                    sideGames: score.roundScores[roundValue-1].sideGames,
                    userID: auth.user?.profile.sub
                });
            }
        });

        if (sideGamesToSave.length > 0) {
            await api.post('/tb/SaveSideGames', {
                sideGames: sideGamesToSave
            })
            .then((res: any) => {
                console.log('Side Games Saved', res.data);
            })
            .catch((err: any) => {
                console.log('Error saving side games', err);
            });
        }

        setAllowSave(false);
        originalViewEventData.current = JSON.parse(JSON.stringify(viewEventData));
    }


    if (!viewEvent) return null;

    return (
        <Stack id='tb-first-tee-container' direction={'column'} spacing={0}
            sx={{ width: '100%', height: '100%', alignItems: 'center',
                overflow: 'hidden', overscrollBehavior: 'none' }}>
                <Stack direction='row' gap={'20px'} width={compHeights.appUsableWidth-60} maxHeight={35}
                    padding={'10px 40px'} alignItems='center' justifyContent='flex-end'>
                    <Typography variant='h6' sx={{ 
                        fontWeight: 500, flex: 1,
                        color: 'primary.main',
                        textAlign: 'left' 
                    }}
                    >First Tee Info and Updates</Typography>
                    {allowSave && 
                        <Button 
                            onClick={() => handleSaveChanges()} 
                            sx={{ 
                                width: 'max-content', 
                                borderRadius: 2, 
                                padding: 0.5, 
                                justifyContent: 'flex-start', 
                                border: '1px solid transparent', 
                                '&:hover': {border: `1px solid ${appTheme.palette.primary.extraLight}`}, 
                                '&:active': {backgroundColor: 'transparent'}, 
                                '&:focus': {backgroundColor: 'transparent'}, 
                            }}
                        >Save Changes</Button> 
                    }
                    <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Round</Typography>
                    <Stack direction='row'  spacing={0} padding={'0px 0px'} alignItems='center' justifyContent='flex-end'>
                        {Array.from({ length: Number(viewEvent.totalRounds) }, (_, index) => index + 1).map((number) => (
                            <RoundButton 
                                key={`${roundValue}-${number}`} 
                                number={number} 
                                roundValue={roundValue} 
                                editMode={editMode} 
                                appTheme={appTheme}
                                factor={factor} 
                                setRoundValue={setRoundValue} 
                            />
                        ))}
                    </Stack>
                </Stack>
                <Stack direction='row' gap={'20px'} width={compHeights.appUsableWidth-60} maxHeight={35}
                    padding={'10px 40px'} alignItems='center' justifyContent='center'>
                    <Button variant='contained' color='primary' 
                        onClick={() => { setTeeTimeIndex(teeTimeIndex - 1); }}
                        disabled={teeTimeIndex === 0}
                    >
                        <Typography variant='subtitle2' sx={{ fontWeight: 500, width: '80px', textAlign: 'center' }}>Previous</Typography>
                    </Button>
                    <Typography variant='h6' 
                        sx={{ fontWeight: 500, width: '200px', textAlign: 'center'}}>
                            {isShotgunRound
                                ? (holeSlots[teeTimeIndex]
                                    ? `Hole ${holeSlots[teeTimeIndex].label}`
                                    : 'No Holes')
                                : (teeTimes[teeTimeIndex]
                                    ? new Date(teeTimes[teeTimeIndex]).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
                                    : 'No Tee Times')}
                        </Typography>
                    <Button variant='contained' color='primary' 
                        onClick={() => { setTeeTimeIndex(teeTimeIndex + 1); }}
                        disabled={teeTimeIndex === (isShotgunRound ? holeSlots.length : teeTimes.length) - 1}
                    >
                        <Typography variant='subtitle2' sx={{ fontWeight: 500, width: '80px', textAlign: 'center' }}>Next</Typography>
                    </Button>
                    {(isShotgunRound ? teeTimes[0] : teeTimes[teeTimeIndex]) && (() => {
                        const currentTeeTime = isShotgunRound ? teeTimes[0] : teeTimes[teeTimeIndex];
                        const currentTeeMs = teeTimeMs(currentTeeTime);
                        const alreadyStarted = paceData.some(
                            (p) => teeTimeMs(p.teeTime) === currentTeeMs && p.startTime,
                        );
                        return (
                            <Button
                                variant='outlined'
                                color='primary'
                                disabled={paceClockStarting || alreadyStarted}
                                onClick={handleStartPaceClock}
                                sx={{ width: 'max-content', minWidth: 140 }}
                            >
                                <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>
                                    {alreadyStarted ? 'Clock Started' : paceClockStarting ? 'Starting…' : 'Start Pace Clock'}
                                </Typography>
                            </Button>
                        );
                    })()}
                </Stack>
                <Stack direction='column' width="90%" >
                    <Stack direction='row' spacing={1} padding={1}
                        sx={{
                            //border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            padding: 1,
                            margin: 1,
                            alignItems: 'center'
                        }}
                    >
                        <Typography variant='subtitle1' flex={2} sx={{ fontWeight: 500 }}>Player</Typography>
                        {teeGroup.some((group: any) => group.teamName) && <Typography variant='subtitle1' flex={2} sx={{ fontWeight: 500, wordWrap: 'break-word', textAlign: 'center' }}>Team</Typography>}
                        <Typography variant='subtitle1' flex={1} sx={{ fontWeight: 500, wordWrap: 'break-word', textAlign: 'center' }}>Score Code</Typography>
                        {roundSideGames.map((game: any) => (
                            <Typography key={game.type} variant='subtitle1' flex={1} 
                                sx={{ fontWeight: 500, textAlign: 'center', wordWrap: 'break-word' }}>
                                {game.type} (${game.cost})
                            </Typography>
                        ))}
                    </Stack>
                    {teeGroup.map((score: any) => (
                        <Stack direction='row' key={score._id}
                            sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                padding: 1,
                                margin: 1,
                                alignItems: 'center'
                            }}
                        >
                            <Typography variant='subtitle1' flex={2} sx={{ fontWeight: 500, wordWrap: 'break-word' }}>{score.playerName}</Typography>
                            {teeGroup.some((group: any) => group.teamName) && <Typography variant='subtitle1' flex={2} sx={{ fontWeight: 500, wordWrap: 'break-word', textAlign: 'center' }}>{score.teamName || ''}</Typography>}
                            <Typography variant='subtitle1' flex={1} sx={{ fontWeight: 500, wordWrap: 'break-word', textAlign: 'center' }}>{score.playerScoreCode}</Typography>
                            {viewEventData.scores.length > 0 && roundSideGames.map((game: any) => {
                                const sideGame = score?.roundScores[roundValue-1]?.sideGames?.find((sg: any) => sg.type === game.type);
                                return (
                                    <Box key={game.type} flex={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Checkbox key={game.type} checked={sideGame || false} 
                                            onChange={(event, checked) => {
                                            const updatedScores = JSON.parse(JSON.stringify(viewEventData.scores));
                                            const scoreIndex = updatedScores.findIndex((s: any) => s._id === score._id);
                                            
                                            if (scoreIndex !== -1) {
                                                if (!updatedScores[scoreIndex].roundScores[roundValue-1].sideGames) {
                                                    updatedScores[scoreIndex].roundScores[roundValue-1].sideGames = [];
                                                }

                                                if (checked) {
                                                    // Add game if checked
                                                    updatedScores[scoreIndex].roundScores[roundValue-1].sideGames.push(game);
                                                } else {
                                                    // Remove game if unchecked
                                                    updatedScores[scoreIndex].roundScores[roundValue-1].sideGames = 
                                                        updatedScores[scoreIndex].roundScores[roundValue-1].sideGames.filter(
                                                            (sg: any) => sg.type !== game.type
                                                        );
                                                }

                                                setViewEventData({
                                                    ...viewEventData,
                                                    scores: updatedScores
                                                });
                                            }
                                            }}
                                        />
                                    </Box>
                                )
                            })}
                        </Stack>
                    ))}
                </Stack>
        </Stack>
    );
};
